// Brand Mode V2 helpers shared by the campaign, deal and messaging pages.
// Pure functions only (tested in src/tests/brandDealUtils.test.js).

export const CACHE_FEE_RATE = 0.1;
export const OFFER_OPEN_DAYS = 7;

export const CAMPAIGN_STATUS = {
  draft: { label: "Draft", tone: "passive" },
  open: { label: "Open", tone: "outline", dot: "#2f6fed" },
  in_contract: { label: "In Contract", tone: "outline" },
  in_progress: { label: "In Progress", tone: "outline" },
  completed: { label: "Completed", tone: "outline" },
  archived: { label: "Archived", tone: "passive" },
  cancelled: { label: "Cancelled", tone: "passive" },
};

export const DEAL_STATUS = {
  draft: { label: "Not contacted", tone: "passive" },
  offer_sent: { label: "Offer sent", tone: "outline", dot: "#b8b8bc" },
  countered: { label: "Countered", tone: "outline", dot: "#edd200" },
  accepted: { label: "Offer accepted", tone: "outline", dot: "#1f9d55" },
  declined: { label: "Declined", tone: "outline", dot: "#e2241a" },
  contract_sent: { label: "Contract sent", tone: "outline", dot: "#2f6fed" },
  signed: { label: "Signed", tone: "outline", dot: "#1f9d55" },
  completed: { label: "Completed", tone: "outline", dot: "#1f9d55" },
  cancelled: { label: "Withdrawn", tone: "passive" },
};

export const SUBMISSION_STATUS = {
  not_posted: { label: "Not Posted", tone: "passive" },
  under_review: { label: "Under Review", tone: "outline", dot: "#edd200" },
  approved: { label: "Approved", tone: "outline", dot: "#1f9d55" },
  revision_requested: { label: "Revision requested", tone: "outline", dot: "#e2241a" },
};

export const DELIVERABLE_STATUS = {
  locked: { label: "Locked", tone: "passive" },
  not_started: { label: "Not started", tone: "passive" },
  in_progress: { label: "In progress", tone: "outline", dot: "#2f6fed" },
  under_review: { label: "Under review", tone: "outline", dot: "#edd200" },
  approved: { label: "Approved", tone: "outline", dot: "#1f9d55" },
};

export const MILESTONE_STATUS = {
  scheduled: { label: "Scheduled", tone: "passive" },
  pending_approval: { label: "Approve & release", tone: "outline", dot: "#edd200" },
  released: { label: "Released", tone: "outline", dot: "#1f9d55" },
};

export const PAYMENT_STRUCTURES = [
  { value: "milestone", label: "Milestone-based (recommended)" },
  { value: "upfront", label: "Paid upfront on signature" },
  { value: "on_completion", label: "Paid on completion" },
];

export const EXCLUSIVITY_SCOPES = [
  { value: "same_category", label: "Same category only" },
  { value: "all_brands", label: "All brand partnerships" },
  { value: "none", label: "No restriction" },
];

export const PLATFORM_OPTIONS = ["Instagram", "TikTok", "X", "YouTube", "Other"];
export const CONTENT_TYPE_OPTIONS = [
  "Post",
  "Stories",
  "Video",
  "Reel",
  "Long form video",
  "Event",
  "Repost",
];
export const AD_USAGE_OPTIONS = [30, 60, 90, 180, 365];

// The next thing a brand can do with a deal, keyed by status. `href` is a
// function of the deal so the manage table can link straight into the flow.
export const dealAction = (deal) => {
  const status = deal?.status || "draft";
  const id = deal?.id;
  switch (status) {
    case "draft":
      return { label: "Send offer", kind: "compose" };
    case "offer_sent":
      return { label: "Follow up", kind: "followup" };
    case "countered":
      return { label: "Review counter", kind: "link", href: `/brand/deals/${id}/counter` };
    case "accepted":
      return { label: "Send contract", kind: "contract" };
    case "contract_sent":
      return deal?.contract?.brandSignedAt
        ? { label: "Awaiting athlete", kind: "link", href: `/brand/deals/${id}` }
        : { label: "Sign contract", kind: "link", href: `/brand/deals/${id}` };
    case "signed":
      return { label: "View details", kind: "link", href: `/brand/deals/${id}` };
    case "completed":
      return { label: "View details", kind: "link", href: `/brand/deals/${id}` };
    case "declined":
      return { label: "View reason", kind: "reason" };
    default:
      return { label: "View", kind: "link", href: `/brand/deals/${id}` };
  }
};

export const campaignAction = (campaign) => {
  const status = campaign?.status || "draft";
  if (status === "draft") return { label: "Open builder", href: `/brand/campaigns/${campaign.id}/edit` };
  if (status === "completed") return { label: "View report", href: `/brand/campaigns/${campaign.id}` };
  return { label: "Manage deals", href: `/brand/campaigns/${campaign.id}` };
};

export const greeting = (date = new Date()) => {
  const h = date.getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

export const initialsOf = (name, surname) => {
  const a = (name || "").trim();
  const b = (surname || "").trim();
  if (a && b) return `${a[0]}${b[0]}`.toUpperCase();
  const parts = a.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (a[0] || "?").toUpperCase();
};

export const fullNameOf = (a) => `${a?.name || ""} ${a?.surname || ""}`.trim();

// A national rank as the Brand and Coach pages say it. The backend ranks an
// athlete only within their own sport, level and gender group
// (users/national_cohorts.py) and sends that group's name (natlBasis,
// "College women's basketball"; "College basketball" when men and women
// had to be pooled). The rank names its group when there is one, so it
// never reads as a rank against every athlete; a group under the minimum
// size sends no rank (null) and says so.
export const NO_NATIONAL_RANK = "No national rank yet";

export const nationalRankLabel = (pct, basis) => {
  if (typeof pct !== "number" || !Number.isFinite(pct)) return NO_NATIONAL_RANK;
  const group = String(basis || "").trim();
  if (!group) return `Top ${pct}% nationally`;
  return `Top ${pct}% of ${group.charAt(0).toLowerCase()}${group.slice(1)}`;
};

const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const s = String(value);
  // Plain YYYY-MM-DD is local, not UTC midnight, so days don't shift.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

// "2h" / "1d" / "3w" (recent activity) and "2 hours ago" (attention rows).
export const timeAgo = (value, { short = false, now = new Date() } = {}) => {
  const d = parseDate(value);
  if (!d) return "";
  const diff = Math.max(0, (now.getTime() - d.getTime()) / 1000);
  const units = [
    ["year", 31536000, "y"],
    ["month", 2592000, "mo"],
    ["week", 604800, "w"],
    ["day", 86400, "d"],
    ["hour", 3600, "h"],
    ["minute", 60, "m"],
  ];
  for (const [name, secs, abbr] of units) {
    if (diff >= secs) {
      const n = Math.floor(diff / secs);
      return short ? `${n}${abbr}` : `${n} ${name}${n === 1 ? "" : "s"} ago`;
    }
  }
  return short ? "now" : "just now";
};

export const fmtDate = (value, { year = true, weekday = false } = {}) => {
  const d = parseDate(value);
  if (!d) return "";
  const opts = { month: "short", day: "numeric" };
  if (year) opts.year = "numeric";
  if (weekday) opts.weekday = "short";
  return d.toLocaleDateString("en-US", opts);
};

export const fmtDateTime = (value) => {
  const d = parseDate(value);
  if (!d) return "";
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, ${d.toLocaleTimeString(
    "en-US",
    { hour: "numeric", minute: "2-digit" },
  )}`;
};

export const fmtDateRange = (start, end) => {
  const s = parseDate(start);
  const e = parseDate(end);
  if (s && e) {
    const sameYear = s.getFullYear() === e.getFullYear();
    return `${fmtDate(s, { year: !sameYear })} – ${fmtDate(e)}`;
  }
  if (s) return `From ${fmtDate(s)}`;
  if (e) return `Until ${fmtDate(e)}`;
  return "";
};

export const fmtLongRange = (start, end) => {
  const s = parseDate(start);
  const e = parseDate(end);
  if (!s || !e) return fmtDateRange(start, end);
  const long = (d, withYear) =>
    d.toLocaleDateString("en-US", { month: "long", day: "numeric", ...(withYear ? { year: "numeric" } : {}) });
  return `${long(s, false)} – ${long(e, true)}`;
};

export const daysUntil = (value, now = new Date()) => {
  const d = parseDate(value);
  if (!d) return null;
  return Math.max(0, Math.ceil((d.getTime() - now.getTime()) / 86400000));
};

export const brandCost = (compensation) => {
  const comp = Math.round(Number(compensation) || 0);
  const fee = Math.round(comp * CACHE_FEE_RATE);
  return { compensation: comp, fee, total: comp + fee };
};

// Mirrors brand_deals.build_milestones so the compose page previews the
// schedule before the server has seen the offer.
export const buildMilestones = (structure, compensation, rows = []) => {
  const comp = Math.round(Number(compensation) || 0);
  if (comp <= 0) return [];
  if (structure === "upfront") return [{ id: "m1", label: "on contract signature", amount: comp }];
  if (structure === "on_completion") {
    return [{ id: "m1", label: "on campaign completion and approval", amount: comp }];
  }
  const first = Math.round(comp * 0.3);
  const second = Math.round(comp * 0.35);
  const third = comp - first - second;
  const units = rows.reduce((s, r) => s + (Number(r.quantity) || 1), 0);
  const half = units ? Math.max(1, Math.floor(units / 2)) : 2;
  return [
    { id: "m1", label: "on contract signature", amount: first },
    { id: "m2", label: `when first ${half} post${half === 1 ? "" : "s"} are delivered and approved`, amount: second },
    { id: "m3", label: "on campaign completion and approval", amount: third },
  ];
};

export const rebalanceMilestones = (milestones, compensation) => {
  const comp = Math.round(Number(compensation) || 0);
  if (!milestones?.length) return buildMilestones("milestone", comp);
  const list = milestones.map((m) => ({ ...m, amount: Math.max(0, Math.round(Number(m.amount) || 0)) }));
  const diff = comp - list.reduce((s, m) => s + m.amount, 0);
  list[list.length - 1].amount = Math.max(0, list[list.length - 1].amount + diff);
  return list;
};

export const deliverableLines = (rows, adUsageDays) => {
  const lines = (rows || [])
    .filter((r) => r.platform || r.contentType)
    .map((r) => {
      const qty = Number(r.quantity) || 1;
      const type = (r.contentType || "Post").toLowerCase();
      let noun = r.contentType || "Post";
      if (type === "post") noun = qty === 1 ? "post" : "posts";
      else if (type === "video") noun = qty === 1 ? "video" : "videos";
      else if (type === "stories") noun = "Stories series";
      const notes = r.notes ? ` (${r.notes})` : "";
      return `${qty} ${r.platform || ""} ${noun}${notes}`.replace(/\s+/g, " ").trim();
    });
  if (adUsageDays) lines.push(`Brand may use content in ads for ${adUsageDays} days`);
  return lines;
};

// Estimated cost band from an athlete's NILx rate card × template rows.
const RATE_KEYS = {
  post: "socialPost",
  stories: "story",
  story: "story",
  video: "reel",
  reel: "reel",
  "long form video": "longVideo",
  event: "events",
  events: "events",
  repost: "share",
  share: "share",
};

export const estimateFromRates = (nilRates, rows) => {
  let total = 0;
  let priced = false;
  (rows || []).forEach((r) => {
    const key = RATE_KEYS[(r.contentType || "").toLowerCase()];
    const rate = key ? Number(nilRates?.[key]) || 0 : 0;
    if (rate > 0) {
      total += rate * (Number(r.quantity) || 1);
      priced = true;
    }
  });
  if (!priced) return null;
  return { min: Math.round(total * 0.85), max: Math.round(total * 1.15) };
};

export const fitStrength = (score) => {
  if (score === null || score === undefined) return { label: "Good", dot: "#2f6fed" };
  if (score >= 70) return { label: "Strong", dot: "#1f9d55" };
  if (score >= 50) return { label: "Good", dot: "#2f6fed" };
  return { label: "Moderate", dot: "#edd200" };
};

// Guidelines compliance for the content approval sidebar: what the
// submission actually contains vs what the deliverable asked for.
export const complianceChecks = ({ submission, deliverable, campaign, brandName }) => {
  const caption = (submission?.caption || "").toLowerCase();
  const checks = [];
  checks.push({
    label: "Sponsored disclosure (#ad or #sponsored)",
    ok: /#ad\b|#sponsored\b|paid partnership/.test(caption),
  });
  const handle = (brandName || "").replace(/\s+/g, "").toLowerCase();
  checks.push({
    label: "Mentions the brand",
    ok: !!handle && (caption.includes(`@${handle}`) || caption.includes(handle) || caption.includes("@")),
  });
  checks.push({
    label: "Proof attached (screenshot or link)",
    ok: !!(submission?.imageUrl || submission?.link),
  });
  if (deliverable?.notes) {
    checks.push({ label: `Brief: ${deliverable.notes}`, ok: null });
  }
  if (campaign?.endDate && submission?.postedAt) {
    checks.push({ label: "Posted inside the campaign window", ok: submission.postedAt <= campaign.endDate });
  }
  return checks;
};

export const counterAnalysis = ({ deal, marketRange }) => {
  const athlete = deal?.athlete || {};
  const counter = deal?.counter || {};
  const offer = deal?.offer || {};
  const comp = Number(counter.compensation) || 0;
  const bits = [];
  if (marketRange?.min && marketRange?.max) {
    const inRange = comp >= marketRange.min && comp <= marketRange.max;
    bits.push(
      inRange
        ? "The counter is within market range."
        : comp > marketRange.max
          ? "The counter is above the rate-card range for these deliverables."
          : "The counter is below the rate-card range for these deliverables.",
    );
    bits.push(
      `${athlete.name || "This athlete"}'s rate card prices comparable deliverables at $${marketRange.min.toLocaleString()}–$${marketRange.max.toLocaleString()}.`,
    );
  } else {
    bits.push("No rate card is available for these deliverables, so benchmark this counter against your budget.");
  }
  if (athlete.cacheScore) {
    bits.push(
      `${athlete.name || "The athlete"}'s CacheScore is ${athlete.cacheScore}${
        athlete.posPct && athlete.posPct < 100 ? `, top ${athlete.posPct}% for the position` : ""
      }.`,
    );
  }
  if ((counter.exclusivityDays ?? offer.exclusivityDays) !== offer.exclusivityDays) {
    bits.push(
      `The exclusivity window moved from ${offer.exclusivityDays} to ${counter.exclusivityDays} days, a common ask from multi-brand athletes.`,
    );
  }
  return bits.join(" ");
};
